/* eslint-disable-next-line import/no-unresolved */
import { useMemo } from 'react';
import { CFD, ETF, FX, FX_QUANTITY_NOTES } from '../constants';
import { useServiceName } from '.';
import { getServiceQuantityUnit } from '../services';
import { useGetBuilderServiceId } from '../services/hooks/builder';

const getQuantityTooltip = (serviceId, isTradeAP) => {
  const tradeName = isTradeAP ? 'トレード「自動売買」' : 'ビルダー';
  if (serviceId === FX) {
    // eslint-disable-next-line max-len
    return `注文ごとの注文数量を指定します。<br>1＝1万通貨 <br>${tradeName}では、最小0.1（万）＝1,000通貨単位の取引が可能です。 <br>${FX_QUANTITY_NOTES}`;
  }
  if (serviceId === ETF) {
    return `注文ごとの注文数量を指定します。<br>${tradeName}では、最小1口の取引が可能です。<br>※TOPIX連動ETFと日経225ETFは10口単位`;
  }
  if (serviceId === CFD) {
    return `注文ごとの注文数量を指定します。<br>${tradeName}では、最小0.1Lotの取引が可能です。`;
  }
  return null;
};

const getRangeTooltip = (serviceId) => {
  if (serviceId === FX) {
    // eslint-disable-next-line max-len
    return '複数の注文を設定する価格帯の上限値または下限値を指定します。<br>【買いの場合】<br>レンジの「上限値」を指定します。なお、レンジの下限は「値幅」分下の価格が表示されます。<br>【売りの場合】<br>レンジの「下限値」を指定します。なお、レンジの上限は「値幅」分上の価格が表示されます。';
  }
  if (serviceId === ETF || serviceId === CFD) {
    // eslint-disable-next-line max-len
    return '複数の注文を設定するレンジの開始位置を指定します。<br>【買いの場合】<br>レンジの「上限値」を指定します。「値幅」分買い下がるように、指定した本数の買い注文が設定されます。<br>【売りの場合】<br>レンジの「下限値」を指定します。「値幅」分売り上がるように、指定した本数の売り注文が設定されます。';
  }
  return null;
};

export const useBuilderTooltips = (isTech = false, isTradeAP = false) => {
  const imagePrefix = useMemo(() => (isTech ? 'tech' : 'multi'), [isTech]);
  const serviceId = useGetBuilderServiceId();
  const serviceName = useServiceName(serviceId);
  const isFX = serviceId === FX;
  const quantityUnit = getServiceQuantityUnit(serviceId);
  const pipsUnit = isFX ? '（pips）' : '';
  return {
    assets: {
      label: '銘柄',
    },
    buySell: {
      label: '売買',
      tooltipMessage:
        '注文の新規取引の方向を選びます。<br>買：価格の上昇で利益を得ます（新規買い → 決済売り）<br>売：価格の下落で利益を得ます（新規売り → 決済買い）',
      descriptionImage: `multi${serviceName}_buySell.png`,
    },
    rangeSpread: {
      label: '値幅',
      tooltipMessage: `複数の注文を設定するレンジの値幅を指定します。`,
      descriptionImage: `${imagePrefix}${serviceName}_rangeSpread.png`,
    },
    itemsCount: {
      label: '本数',
      // eslint-disable-next-line max-len
      tooltipMessage: `指定したレンジの値幅の中に何本の注文を設定するかを指定します。注文は等間隔に設定されます。<br>注文間隔${pipsUnit}＝値幅÷（本数-1）`,
      descriptionImage: `${imagePrefix}${serviceName}_items_count.png`,
    },
    quantity: {
      label: `数量(${quantityUnit})`,
      tooltipMessage: getQuantityTooltip(serviceId, isTradeAP),
      descriptionImage: `multi${serviceName}_quantity.png`,
    },
    entryPricePipsValue: {
      label: 'レンジ',
      tooltipMessage: getRangeTooltip(serviceId),
      descriptionImage: `multi${serviceName}_entry.png`,
    },
    ocoCheckbox: {
      label: 'OCO設定',
      // eslint-disable-next-line max-len, prettier/prettier
      // eslint-disable-next-line max-len
      tooltipMessage:
        // eslint-disable-next-line max-len
        'ONに設定した場合は、レンジの上限値（買いの場合）または下限値（売りの場合）を挟んで、指定した値幅の2倍の範囲に初回注文をOCOで設定します。<br>OCO注文とは、1つの注文で2つの価格に同時に発注する手法です（指値・逆指値）。どちらかが成立すると、もう一方は自動的にキャンセルとなります。',
      descriptionImage: `multi${serviceName}_entry_oco.png`,
    },
    profitMargin: {
      label: '利確幅', // eslint-disable-next-line max-len
      tooltipMessage: `保有中の建玉が買った（売った）価格から利益の方向にどのくらい相場が動いたら利益確定（利確）するかを値幅${pipsUnit}で指定します。`,
      descriptionImage: `multi${serviceName}_profitMargin.png`,
    },
    stopLossCheckbox: {
      label: '損切幅', // eslint-disable-next-line max-len
      tooltipMessage: `保有中の建玉が買った（売った）価格からどのくらい損失の方向に相場が動いたら損失確定（損切り）するかを値幅${pipsUnit}で指定します。`,
      descriptionImage: `multi${serviceName}_stopLoss.png`,
    },
    followCheckbox: {
      label: 'フォロー設定',
      tooltipMessage: 'ONに設定した場合は、2回目以降の新規注文における順張り方向の取引条件を設定します。', // eslint-disable-next-line max-len
      descriptionImage: `multi${serviceName}_follow.png`,
    },
    followInput: {
      label: 'フォロー値',
      // eslint-disable-next-line max-len
      tooltipMessage: `【買いの場合】<br>決済価格からいくら上がったら、再び取引をするのか、決済価格からの値幅を指定します。<br>【売りの場合】<br>決済価格からいくら下がったら、再び取引をするのか、決済価格からの値幅を指定します。`,
      descriptionImage: `multi${serviceName}_follow.png`,
    },
    counterCheckbox: {
      label: 'カウンター設定',
      tooltipMessage: 'ONに設定した場合は、2回目以降の新規注文における逆張り方向の取引条件を設定します。',
      descriptionImage: `multi${serviceName}_counter.png`,
    },
    counterInput: {
      label: 'カウンター値',
      // eslint-disable-next-line max-len
      tooltipMessage: `【買いの場合】<br>決済価格からいくら下がったら、再び取引をするのか、決済価格からの値幅を指定します。<br>【売りの場合】<br>決済価格からいくら上がったら、再び取引をするのか、決済価格からの値幅を指定します。`,
      descriptionImage: `multi${serviceName}_counter.png`,
    },
    counterFixedCheckbox: {
      label: '固定する',
      tooltipMessage:
        // eslint-disable-next-line max-len, prettier/prettier
        'ONに設定した場合は、カウンター値が固定価格で設定されます。2回目以降の新規注文は毎回固定された価格で発注を繰り返します。<br>OFFに設定した場合は、2回目以降の新規注文は、決済価格からカウンター値の値幅分離れたところに発注します。',
      // eslint-disable-next-line max-len, prettier/prettier
      descriptionImage: `multi${serviceName}_counter_fix.png`,
    },
  };
};
